/**
 * The learning-path card for a CATALOGUE grid — the shape a path takes on /paths.
 *
 * WHY THIS FILE EXISTS. The card was written inside `Paths.tsx` as a private function, which
 * was correct while the browse page was the only grid of paths. It was then copied here so a
 * second grid could use it, and for a while both copies existed: `Paths.tsx` was being edited
 * by another agent at the time and could not be changed underneath them. That is over —
 * `Paths.tsx` now imports this file and its copy is deleted, so there is exactly one
 * definition of what an "in progress" path looks like in a catalogue.
 *
 * WHAT IT IS NOT. It is not the dashboard's card any more. The dashboard's "My Learning"
 * library holds paths and courses in ONE grid under one heading, so both have to be the same
 * shape — `MyLearning.tsx` renders the prototype's `.prog-card` for both. Two shapes in one
 * grid is exactly what that single card exists to avoid. This card stays because /paths is a
 * grid of paths ALONE, sitting one click from Discover's grid of courses alone, and there the
 * argument runs the other way round.
 *
 * IT IS DISCOVER'S CARD, deliberately. `course-card` is the catalogue's class and `path-card`
 * adds only what a path has and a course does not. A card that is NEARLY the same shape as
 * the one a click away reads as a mistake; one that is EXACTLY the same shape reads as the
 * same product.
 *
 * Colour comes from `paths.css` and `learner.css`, both of which are tokens only — there is
 * no palette, host, or organisation name anywhere in this file.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '../../shared/ui';
import { pathHref, pct, type LearningPathView } from '../../shared/lib/paths';
import { pathwayPriceView, type PricingSeam } from '../../shared/lib/pricing';
import { CardSaving, PriceTag } from '../../shared/ui/price';

/** One path, as a catalogue card. */
export function PathCard({
  view,
  prices,
}: {
  view: LearningPathView;
  /** The grid's pathway price batch. `undefined` resolves to `unknown`, never to free. */
  prices?: PricingSeam;
}) {
  const { path, state, stepCount, stepsCompleted, progress } = view;

  /**
   * PRICE AND STATE SHARE THE SLOT AND NEVER BOTH APPEAR (spec R6.8, OWNED).
   *
   * `PathStateChip` renders "Enrolled" / "In progress" / "Completed" and returns null for
   * `not_enrolled`, so the price fills exactly the gap it leaves. A learner who is in the
   * path does not need to be told what it costs — they already hold it — and printing both
   * would put a price on something the same row says you own.
   */
  const price = state === 'not_enrolled' ? pathwayPriceView(prices, path.key) : null;

  return (
    <Link to={pathHref(path.slug)} className="course-card path-card">
      <PathThumb view={view} />

      <div className="course-card__body">
        <span className="course-card__org">
          <Icon name="route" />
          {stepCount} {stepCount === 1 ? 'course' : 'courses'}
        </span>
        <h3>{path.title}</h3>
        <p>{path.description}</p>

        {/* Only for a learner who is actually in the path. A 0% bar on a path you have
            never joined reads as "you have fallen behind" rather than "you have not
            started", which is the opposite of an invitation. */}
        {state !== 'not_enrolled' && (
          <div className="path-progress">
            <div className="path-progress__row">
              <span>
                {stepsCompleted} of {stepCount} complete
              </span>
              <strong>{pct(progress)}%</strong>
            </div>
            <div className="path-bar">
              <div
                className="path-bar__fill"
                data-done={state === 'completed' || undefined}
                style={{ width: `${pct(progress)}%` }}
              />
            </div>
          </div>
        )}

        <div className="course-card__meta">
          {/* First in the row, as on Discover's course card: the price is the fact that
              decides whether the reader opens the card at all. `CardSaving` adds the struck
              buy-separately total only when the saving is real — a `fixed` bundle priced
              above its parts makes no claim here (R5.5, AC-011). */}
          {price && (
            <>
              <PriceTag view={price} />
              <CardSaving view={price} />
            </>
          )}
          <span>
            <Icon name={path.sequential ? 'linear_scale' : 'apps'} />
            {path.sequential ? 'In sequence' : 'Any order'}
          </span>
          {path.invite_only && (
            <span>
              <Icon name="lock" />
              Invitation only
            </span>
          )}
          <PathStateChip state={state} />
        </div>
      </div>
    </Link>
  );
}

/** Where the learner stands, in one word. Absent when they are not in the path at all. */
export function PathStateChip({ state }: { state: LearningPathView['state'] }) {
  if (state === 'not_enrolled') return null;

  const label =
    state === 'completed' ? 'Completed' : state === 'in_progress' ? 'In progress' : 'Enrolled';

  return (
    <span className="path-state" data-state={state}>
      <Icon name={state === 'completed' ? 'verified' : 'check_circle'} />
      {label}
    </span>
  );
}

/**
 * Card artwork.
 *
 * A path has no image of its own until an author uploads one, so the fallback is the
 * academy's brand tile with the path's initial — the same treatment Discover gives a
 * course with no artwork, for the same reason.
 */
export function PathThumb({ view }: { view: LearningPathView }) {
  const [failed, setFailed] = useState(false);
  const { image, title } = view.path;

  if (image && !failed) {
    return (
      <img
        className="course-card__thumb"
        src={image}
        alt=""
        loading="lazy"
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <div className="course-card__thumb course-card__thumb--letter" aria-hidden="true">
      {title.charAt(0).toUpperCase()}
    </div>
  );
}
